<script setup lang="ts">
import EntityCustomerSelect from '@/components/asicw/common/EntityCustomerSelect.vue'
import { useBalanceTransactions } from '@/composables/asicw/balance'
import { getCustomerDisplayName } from '@/helpers/core/customers'
import { NotifyFail } from '@/helpers/core/notifications'
import { dateFormatter } from '@/helpers/ep/formatters'
import type { IBalanceTransaction, IBalanceTransactionFilters } from '@/types/asicw/balance'
import { ClaimCostCharges } from '@/types/asicw/claims'
import { InvoiceType } from '@/types/asicw/invoices'
import type { ICustomer } from '@/types/core/Customer'
import { downloadFile, getCostColor } from '@/util'
import { formatCurrency, tableColumnCurrency } from '@/util/formatters.ts'
import { Icon } from '@iconify/vue'
import { watchDebounced } from '@vueuse/core'
import { DateTime } from 'luxon'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const filters = reactive<IBalanceTransactionFilters>({
  policy_group_number: undefined,
  claim_number: undefined,
  start_datetime: undefined,
  end_datetime: undefined,
  charge: undefined,
  entity: undefined,
  customer_id: undefined,
})

const claimNumberInput = ref<string>()
const policyGroupInput = ref<string>()
const dateRange = ref<[Date, Date] | null>(null)
const selectedEntityValue = ref<string>()

const router = useRouter()

// Check if required filter is selected (only entity is required now)
const areRequiredFiltersSelected = computed(() => {
  return !!selectedEntityValue.value?.trim()
})

// Vue Query - only enabled when required filters are selected
const { getBalanceTransactionsQuery, exportBalanceTransactionsMutation } = useBalanceTransactions()
const { isFetching, isError, isRefetching, data, error, refetch } = getBalanceTransactionsQuery(
  filters,
  areRequiredFiltersSelected
)

watchDebounced(
  claimNumberInput,
  (value) => {
    filters.claim_number = value || undefined
  },
  { debounce: 500 }
)

watchDebounced(
  policyGroupInput,
  (value) => {
    filters.policy_group_number = value || undefined
  },
  { debounce: 500 }
)

const handleDateRangeChange = (newRange: Array<string> | null) => {
  if (newRange?.length !== 2) {
    filters.start_datetime = undefined
    filters.end_datetime = undefined
    return
  }

  const startDate = newRange[0]
  const endDate = newRange[1]

  // Use Luxon to parse and validate the date strings
  const startDateTime = DateTime.fromISO(startDate)
  const endDateTime = DateTime.fromISO(endDate)

  // Send date-only strings in YYYY-MM-DD format
  filters.start_datetime = startDateTime.isValid ? startDateTime.toFormat('yyyy-MM-dd') : undefined
  filters.end_datetime = endDateTime.isValid ? endDateTime.toFormat('yyyy-MM-dd') : undefined
}

const handleEntityChange = ({ entity, customer }: { entity: string; customer?: ICustomer }) => {
  filters.entity = entity
  filters.customer_id = customer?.id
  selectedEntityValue.value = customer ? getCustomerDisplayName(customer) : entity
}

const handleExport = async () => {
  try {
    downloadFile(await exportBalanceTransactionsMutation.mutateAsync(filters), `balance_transactions.csv`)
  } catch (err: any) {
    NotifyFail(`Failed to download balance sheet: ${err.message}`)
  }
}

const handleInvoiceClick = (row: IBalanceTransaction, invoiceID: string) => {
  if (row.claim_number) {
    router.push({
      name: 'asicw-claim-invoices',
      query: { id: invoiceID },
    })
  } else {
    router.push({
      name: 'asicw-premium-invoices',
      query: { id: invoiceID },
    })
  }
}

// Calculate total cost for summary row
const totalCost = computed(() => {
  if (!data.value?.length) return 0
  return data.value.reduce((sum, transaction) => sum + (transaction.cost ?? 0), 0)
})

// Summary method for the table
const getSummaries = (param: any) => {
  const { columns } = param
  const sums: string[] = []

  columns.forEach((column: any, index: number) => {
    if (index === 0) {
      sums[index] = 'Total'
    } else if (column.property === 'cost') {
      sums[index] = tableColumnCurrency(null, null, totalCost.value)
    } else {
      sums[index] = ''
    }
  })

  return sums
}
</script>

<template>
  <RlErrorResult v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
    </el-button>
  </RlErrorResult>
  <template v-else>
    <div class="flex justify-between mb-4">
      <div class="flex gap-2 items-center flex-wrap max-h-20 overflow-hidden">
        <EntityCustomerSelect
          v-model="selectedEntityValue"
          placeholder="Entity"
          style="width: 200px"
          :disabled="isFetching"
          @change="handleEntityChange"
        />
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="to"
          start-placeholder="Start Date"
          end-placeholder="End Date"
          format="MM/DD/YYYY"
          value-format="YYYY-MM-DD"
          style="width: 280px"
          :disabled="isFetching || !areRequiredFiltersSelected"
          @change="handleDateRangeChange"
        />
        <el-input
          v-model="policyGroupInput"
          placeholder="Policy Group Number"
          clearable
          style="width: 200px"
          :disabled="isFetching || !areRequiredFiltersSelected"
        />
        <el-input
          v-model="claimNumberInput"
          placeholder="Claim Number"
          clearable
          style="width: 200px"
          :disabled="isFetching || !areRequiredFiltersSelected"
        />
        <el-select
          v-model="filters.charge"
          placeholder="Charge"
          clearable
          style="width: 200px"
          :disabled="isFetching || !areRequiredFiltersSelected"
        >
          <el-option
            v-for="value in [...Object.values(ClaimCostCharges), ...Object.values(InvoiceType)].sort()"
            :key="value"
            :label="value"
            :value="value"
          />
        </el-select>
      </div>
      <div class="flex gap-2">
        <el-button
          :disabled="!areRequiredFiltersSelected || isFetching || exportBalanceTransactionsMutation.isPending.value"
          @click="handleExport"
        >
          Export
        </el-button>
      </div>
    </div>

    <!-- Validation message -->
    <div v-if="!areRequiredFiltersSelected" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
      <div class="flex items-center">
        <Icon icon="material-symbols:info-rounded" class="text-blue-600 mr-2" />
        <div class="text-blue-800 text-sm">
          <p>To view balance transactions, please select an entity (Administrator, Servicer or any customer).</p>
          <p class="mt-1 text-xs opacity-75">
            Date range and other filters are optional and can be applied to narrow down the results.
          </p>
        </div>
      </div>
    </div>
    <el-table
      v-loading="isFetching"
      :data="areRequiredFiltersSelected ? data : []"
      :empty-text="areRequiredFiltersSelected ? 'No data' : 'Please select an entity to search'"
      stripe
      show-summary
      :summary-method="getSummaries"
      max-height="700"
    >
      <el-table-column prop="policy_group_number" label="Policy Group Number" :min-width="150" />
      <el-table-column prop="claim_number" label="Claim Number" :min-width="120" />
      <el-table-column prop="date" label="Date" :min-width="120" :formatter="dateFormatter" />
      <el-table-column prop="charge" label="Charge" :min-width="150" />
      <el-table-column prop="cost" label="Cost" :min-width="100">
        <template #default="scope">
          <span :style="{ color: getCostColor(scope.row.cost) }">
            {{ formatCurrency(scope.row.cost) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="payable_from" label="Payable From (Payer)" :min-width="150" />
      <el-table-column prop="payable_to" label="Payable To (Payee)" :min-width="150" />
      <el-table-column label="Invoices" :min-width="120">
        <template #default="scope">
          <div class="flex flex-wrap gap-1">
            <el-tag
              v-for="invoiceID in scope.row.invoices"
              :key="invoiceID"
              size="small"
              class="cursor-pointer hover:opacity-80"
              @click="handleInvoiceClick(scope.row as IBalanceTransaction, invoiceID)"
            >
              {{ invoiceID }}
            </el-tag>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </template>
</template>
